import { useEffect, useRef } from 'react'
import {
  SWIPE_HORIZONTAL_THRESHOLD_PX,
  SWIPE_EDGE_ZONE_PX,
} from '@/shared/constants/gestures'

/**
 * Intercepts the mobile back-swipe gesture (from left edge) and the browser
 * back button / hardware back, calling `onBack` instead of navigating away.
 * Only active when `enabled` is true.
 */
export function useBackGesture(onBack: () => void, enabled: boolean = true) {
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  // Push a history guard so browser/hardware back fires popstate instead of leaving
  useEffect(() => {
    if (!enabled) return

    window.history.pushState({ backGestureGuard: true }, '')

    const handlePopState = () => {
      window.history.pushState({ backGestureGuard: true }, '')
      onBackRef.current()
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [enabled])

  // Swipe right from left edge (the standard iOS/Android back gesture)
  useEffect(() => {
    if (!enabled) return

    let startX = 0
    let startY = 0

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      if (!touch) return
      startX = touch.clientX
      startY = touch.clientY
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0]
      if (!touch) return
      const dx = touch.clientX - startX
      const dy = touch.clientY - startY

      if (
        startX <= SWIPE_EDGE_ZONE_PX &&
        dx > SWIPE_HORIZONTAL_THRESHOLD_PX &&
        Math.abs(dy) < Math.abs(dx)
      ) {
        onBackRef.current()
      }
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [enabled])
}
